import React from 'react';
import styled from 'styled-components';
import { FiCircle, FiHexagon, FiLayers, FiZap, FiTrendingUp, FiGrid, FiSettings } from 'react-icons/fi';

const Section = styled.div`
  margin-bottom: 1.25rem;
  h3 {
    margin: 0 0 1rem;
    color: #fff;
    font-size: 1.25rem;
    font-weight: 700;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
`;

const Card = styled.button`
  display: block;
  width: 100%;
  text-align: center;
  cursor: pointer;
  padding: 1.35rem 1rem 1.15rem;
  border-radius: 12px;
  border: 1px solid ${(p) => (p.$selected ? 'rgba(59, 130, 246, 0.85)' : 'rgba(255, 255, 255, 0.1)')};
  background: ${(p) => (p.$selected ? 'rgba(59, 130, 246, 0.08)' : '#161b33')};
  box-shadow: ${(p) => (p.$selected ? '0 0 0 1px rgba(59, 130, 246, 0.35)' : 'none')};
  transition: border-color 0.2s ease, transform 0.2s ease, background 0.2s ease;
  &:hover {
    border-color: rgba(59, 130, 246, 0.55);
    transform: translateY(-2px);
  }
  &:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }
`;

const IconWrap = styled.div`
  font-size: 2.75rem;
  color: #3b82f6;
  margin-bottom: 0.65rem;
  display: flex;
  justify-content: center;
`;

const Title = styled.div`
  font-size: 1.05rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.35rem;
  line-height: 1.25;
`;

const BrandStd = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  color: #3b82f6;
  margin-bottom: 0.55rem;
`;

const Desc = styled.p`
  margin: 0 0 1rem;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: #94a3b8;
`;

const Features = styled.div`
  display: flex;
  justify-content: space-around;
  gap: 0.5rem;
  font-size: 0.8125rem;
`;

const Feature = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
  .label {
    color: #64748b;
    font-weight: 500;
  }
`;

const YesNo = styled.span`
  font-weight: 600;
  color: ${(p) => (p.$yes ? '#22c55e' : '#ef4444')};
`;

function TypeIcon({ name }) {
  switch (name) {
    case 'circle':
      return <FiCircle />;
    case 'hexagon':
      return <FiHexagon />;
    case 'layers':
      return <FiLayers />;
    case 'zap':
      return <FiZap />;
    case 'trending':
      return <FiTrendingUp />;
    case 'grid':
      return <FiGrid />;
    case 'settings':
      return <FiSettings />;
    default:
      return <FiHexagon />;
  }
}

/** @param {{ types: ReadonlyArray<Record<string, unknown>>, selectedId: string, onSelect: (id: string) => void }} props */
export function DeployTokenTypeGrid({ types, selectedId, onSelect }) {
  return (
    <Section>
      <h3>Choose token type</h3>
      <Grid>
        {types.map((t) => (
          <Card
            key={t.id}
            type="button"
            $selected={selectedId === t.id}
            onClick={() => onSelect(t.id)}
            aria-pressed={selectedId === t.id}
          >
            <IconWrap aria-hidden>
              <TypeIcon name={t.icon} />
            </IconWrap>
            <Title>{t.name}</Title>
            <BrandStd>
              {t.brandStandard}
              {t.ercStandard && t.ercStandard !== t.brandStandard ? (
                <span style={{ color: '#64748b', fontWeight: 500, fontSize: '0.78rem' }}> · {t.ercStandard}</span>
              ) : null}
            </BrandStd>
            <Desc>{t.description}</Desc>
            <Features>
              <Feature>
                <span className="label">Divisible</span>
                <YesNo $yes={t.divisible}>{t.divisible ? 'Yes' : 'No'}</YesNo>
              </Feature>
              <Feature>
                <span className="label">Unique</span>
                <YesNo $yes={t.unique}>{t.unique ? 'Yes' : 'No'}</YesNo>
              </Feature>
            </Features>
          </Card>
        ))}
      </Grid>
    </Section>
  );
}
